/** Integrations > LeadPro: the search key behind the Leads page's plain-English search. Owner/admin only (the page gates it). */
import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CheckCircle2, Loader2, Search } from "lucide-react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";

interface LeadProStatus {
  configured: boolean;
  /** organization = saved here; server = the server's own key is in use; none = nothing set up */
  source: "organization" | "server" | "none";
  maskedToken?: string;
}

const QUERY_KEY = ["/api/organizations/leadpro"];

export function LeadProSettingsCard() {
  const { toast } = useToast();
  const [token, setToken] = useState("");

  const { data, isLoading } = useQuery<LeadProStatus>({ queryKey: QUERY_KEY });

  const saveMutation = useMutation({
    mutationFn: async (apiToken: string) => (await apiRequest("POST", "/api/organizations/leadpro", { apiToken })).json(),
    onSuccess: () => {
      setToken("");
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ["prospect-leads", "search-status"] });
      toast({ title: "LeadPro key saved", description: "The Leads page can search with it now." });
    },
    onError: (e: any) => toast({ title: "Could not save the key", description: e?.message, variant: "destructive" }),
  });

  const removeMutation = useMutation({
    mutationFn: async () => (await apiRequest("DELETE", "/api/organizations/leadpro")).json(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ["prospect-leads", "search-status"] });
      toast({ title: "LeadPro key removed" });
    },
    onError: (e: any) => toast({ title: "Could not remove the key", description: e?.message, variant: "destructive" }),
  });

  const ownKey = data?.source === "organization";

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
            <Search className="h-6 w-6 text-white" />
          </div>
          <div>
            <CardTitle className="flex items-center gap-2">
              LeadPro
              {data?.configured && (
                <Badge variant="default" className="ml-2">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  {ownKey ? "Connected" : "Using the platform key"}
                </Badge>
              )}
            </CardTitle>
            <CardDescription>
              Powers plain-English lead search on the Leads page. Use a key with the admin:ask permission.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <>
            {ownKey && (
              <p className="text-sm text-muted-foreground">
                Saved key: <span className="font-mono">{data?.maskedToken}</span>
              </p>
            )}
            {data?.source === "server" && (
              <p className="text-sm text-muted-foreground">
                No key of your own is saved, so searches use the platform's key. Add yours to use it instead.
              </p>
            )}
            <div className="space-y-2">
              <Label htmlFor="leadpro-token">{ownKey ? "Replace key" : "LeadPro key"}</Label>
              <Input
                id="leadpro-token"
                type="password"
                autoComplete="off"
                placeholder="Paste your LeadPro key"
                value={token}
                onChange={(e) => setToken(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Checked with LeadPro before it is saved, stored encrypted, and never shown again.
              </p>
            </div>
          </>
        )}
      </CardContent>
      <CardFooter className="gap-2">
        <Button onClick={() => saveMutation.mutate(token.trim())} disabled={!token.trim() || saveMutation.isPending}>
          {saveMutation.isPending ? "Checking..." : "Save"}
        </Button>
        {ownKey && (
          <Button variant="outline" onClick={() => removeMutation.mutate()} disabled={removeMutation.isPending}>
            Remove key
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}
